import dayjs from '@/lib/dayjs'
import type { Address } from '@/lib/services/addresses/addresses.types'
import { getLocationName } from '@/lib/services/locations/locations.utils'
import type { SortingState } from '@tanstack/react-table'
import type { Dayjs } from 'dayjs'
import type {
  AvailabilityCalendarOccurrence,
  AvailabilityChange,
  AvailabilityDateDisplay,
  AvailabilityTemporaryHours,
  AvailabilityTemporaryHoursWindow,
  AvailabilityTemporaryHoursWindowInput,
} from './availability-changes.types'

export const INDEFINITE_AVAILABILITY_CHANGE_END_DATE = '2099-12-31'
export const INDEFINITE_AVAILABILITY_CHANGE_DISPLAY_HORIZON_DAYS = 365
export const INDEFINITE_AVAILABILITY_CHANGE_END_LABEL = 'Until further notice'
export const AVAILABILITY_CHANGE_DATE_DISPLAY_END_KIND_FINITE =
  'finite' satisfies AvailabilityDateDisplay['end_kind']
export const AVAILABILITY_CHANGE_DATE_DISPLAY_END_KIND_STORED_INDEFINITE =
  'stored_indefinite' satisfies AvailabilityDateDisplay['end_kind']
export const AVAILABILITY_CHANGE_DATE_DISPLAY_END_KIND_DISPLAY_INDEFINITE =
  'display_indefinite' satisfies AvailabilityDateDisplay['end_kind']

/**
 * A location the helpers match by id and read the name and timezone of: a `Location` or a
 * `LocationListItem`, the shape availability payloads name their locations in.
 */
export interface AvailabilityLocationRef {
  id: number
  address?: Pick<Address, 'name' | 'line1' | 'timezone'> | null
}

/**
 * The change fields these helpers read. They take saved changes (`AvailabilityChange`), calendar
 * occurrences (`AvailabilityCalendarOccurrence`), and occurrences resolved from either, which each
 * carry a different subset, so every key is optional.
 */
export interface AvailabilityChangeFields {
  /** A saved change's id, or a calendar occurrence's `{change_id}:{location_id}:{Ymd}`. */
  id?: number | string
  /** Calendar occurrences: the saved change's id. */
  change_id?: number
  /** Calendar occurrences: the location the occurrence is at. */
  location?: AvailabilityLocationRef
  /** Every location the change applies to. */
  locations?: AvailabilityLocationRef[]
  location_count?: number
  total_active_locations?: number
  all_locations?: boolean
  /** Saved changes: `YYYY-MM-DD`. */
  start_date?: string | null
  end_date?: string | null
  /** Calendar occurrences: the `YYYY-MM-DD` the occurrence starts. */
  date?: string
  /** ISO 8601 with an offset. */
  starts_at?: string | null
  ends_at?: string | null
  date_display?: AvailabilityDateDisplay | null
  /**
   * The storefront availability notice's flag, which this helper mirrors. Admin payloads send
   * `date_display` instead.
   */
  ends_indefinitely?: boolean
  repeats_annually?: boolean
  holiday_key?: string | null
  effect_labels?: string[]
  store_closed?: boolean
  pickup_paused?: boolean
  delivery_paused?: boolean
  designers_choice_only?: boolean
  temporary_hours?: AvailabilityTemporaryHours | null
}

/** A change resolved to one occurrence's window. */
export type AvailabilityChangeOccurrence = AvailabilityChangeFields & {
  starts_at: string
  ends_at: string
}

/** A location filter option: `id:{id}`, or `name:{name}` for a location without an id. */
export interface AvailabilityLocationEntry {
  value: string
  label: string
}

/** The local window of one occurrence. */
interface OccurrenceRange {
  startsAt: Dayjs
  endsAt: Dayjs
}

function isFilled(value: string | null | undefined): value is string {
  return Boolean(value)
}

export function getChangeLocationNames(change: AvailabilityChangeFields): string[] {
  return (change.locations ?? []).map(getLocationName)
}

export function getChangeLocationEntries(
  change: AvailabilityChangeFields
): AvailabilityLocationEntry[] {
  if (change.location) {
    return [{ value: `id:${change.location.id}`, label: getLocationName(change.location) }]
  }

  return (change.locations ?? []).map((location) => ({
    value: `id:${location.id}`,
    label: getLocationName(location),
  }))
}

function getStoredChangeSelectedLocationIds(
  change: AvailabilityChangeFields | null | undefined
): string[] {
  if (change?.locations?.length) {
    return change.locations.map((location) => String(location.id))
  }

  return change?.location ? [String(change.location.id)] : []
}

export function getChangeSelectedLocationIds(
  change: AvailabilityChangeFields | null | undefined,
  activeLocationIds: string[] = []
): string[] {
  if (!change) {
    return activeLocationIds
  }

  if (change.all_locations) {
    return activeLocationIds
  }

  const storedLocationIds = getStoredChangeSelectedLocationIds(change)

  return storedLocationIds.length > 0 ? storedLocationIds : activeLocationIds
}

export function getActiveChangeSelectedLocationIds(
  change: AvailabilityChangeFields | null | undefined,
  activeLocationIds: string[] = []
): string[] {
  const normalizedActiveLocationIds = activeLocationIds.map(String).filter(Boolean)

  if (normalizedActiveLocationIds.length === 0) {
    return getStoredChangeSelectedLocationIds(change)
  }

  const activeLocationIdSet = new Set(normalizedActiveLocationIds)
  const selectedActiveLocationIds = getChangeSelectedLocationIds(
    change,
    normalizedActiveLocationIds
  ).filter((locationId) => activeLocationIdSet.has(String(locationId)))
  const storedLocationIds = getStoredChangeSelectedLocationIds(change)

  return selectedActiveLocationIds.length > 0
    ? selectedActiveLocationIds
    : storedLocationIds.length > 0
      ? storedLocationIds
      : normalizedActiveLocationIds
}

function getLocationTimezone(location: AvailabilityLocationRef): string | null {
  return location?.address?.timezone ?? null
}

function getChangeMatchingActiveLocations<TLocation extends AvailabilityLocationRef>(
  change: AvailabilityChangeFields,
  activeLocations: TLocation[] = []
): TLocation[] {
  const activeLocationIds = activeLocations.map((location) => String(location.id)).filter(Boolean)
  const selectedLocationIds = new Set(getChangeSelectedLocationIds(change, activeLocationIds))

  return activeLocations.filter((location) => selectedLocationIds.has(String(location.id)))
}

export function getChangeLocationTimezones(
  change: AvailabilityChangeFields,
  activeLocations: AvailabilityLocationRef[] = []
): string[] {
  const matchingActiveLocations = getChangeMatchingActiveLocations(change, activeLocations)
  const matchingActiveTimezones = matchingActiveLocations.map(getLocationTimezone).filter(isFilled)

  if (matchingActiveTimezones.length > 0) {
    return [...new Set(matchingActiveTimezones)]
  }

  const changeLocations = Array.isArray(change?.locations) ? change.locations : []
  const changeTimezones = changeLocations.map(getLocationTimezone).filter(isFilled)

  return [...new Set(changeTimezones)]
}

/** Whether the change's location count reaches every active location. */
function changeCoversActiveLocationCount(change: AvailabilityChangeFields): boolean {
  const totalActiveLocations = change.total_active_locations ?? 0

  return totalActiveLocations > 0 && (change.location_count ?? 0) >= totalActiveLocations
}

export function getChangeLocationLabel(change: AvailabilityChangeFields): string {
  if (change.all_locations || changeCoversActiveLocationCount(change)) {
    return 'All'
  }

  const locationNames = getChangeLocationNames(change)
  const [firstLocationName] = locationNames

  if (firstLocationName === undefined) {
    return 'All selected locations'
  }

  if (locationNames.length === 1) {
    return firstLocationName
  }

  return `${locationNames.length} locations`
}

export function getChangeLocationPreview(change: AvailabilityChangeFields): string | null {
  if (change.all_locations || changeCoversActiveLocationCount(change)) {
    return null
  }

  const locationNames = getChangeLocationNames(change)

  if (locationNames.length <= 1) {
    return null
  }

  const visibleLocationNames = locationNames.slice(0, 2).join(', ')
  const hiddenLocationCount = locationNames.length - 2

  return hiddenLocationCount > 0
    ? `${visibleLocationNames} +${hiddenLocationCount} more`
    : visibleLocationNames
}

export function getChangeLocationSummary(change: AvailabilityChangeFields): string {
  const locationLabel = getChangeLocationLabel(change)
  const locationPreview = getChangeLocationPreview(change)

  return locationPreview ? `${locationLabel}: ${locationPreview}` : locationLabel
}

export function formatAvailabilityDate(date: string | null | undefined): string | null {
  if (!date) {
    return null
  }

  const parsedDate = dayjs(date)

  return parsedDate.isValid() ? parsedDate.format('MMM D, YYYY') : date
}

function getOrdinalDaySuffix(day: number): string {
  if (day % 100 >= 11 && day % 100 <= 13) {
    return 'th'
  }

  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

export function formatAvailabilityOrdinalDate(date: string | null | undefined): string | null {
  if (!date) {
    return null
  }

  const parsedDate = dayjs(date)

  if (!parsedDate.isValid()) {
    return date
  }

  const day = parsedDate.date()

  return `${parsedDate.format('MMM')} ${day}${getOrdinalDaySuffix(day)}, ${parsedDate.format('YYYY')}`
}

export function formatAvailabilityOrdinalDateTime(date: string | null | undefined): string | null {
  if (!date) {
    return null
  }

  const parsedDate = dayjs(date)

  if (!parsedDate.isValid()) {
    return date
  }

  return `${formatAvailabilityOrdinalDate(date)} at ${parsedDate.format('h:mm A')}`
}

export function getChangeStartDate(
  change: AvailabilityChangeFields | null | undefined
): string | null | undefined {
  return change?.start_date ?? change?.date ?? change?.starts_at
}

export function getChangeEndDate(
  change: AvailabilityChangeFields | null | undefined
): string | null | undefined {
  return change?.end_date ?? change?.ends_at ?? change?.date ?? getChangeStartDate(change)
}

/** The `YYYY-MM-DD` a date or offset datetime falls on, in the datetime's own offset. */
export function getChangeDateInputValue(value: string | null | undefined): string | null {
  if (!value) {
    return null
  }

  const dateOnly = value.match(/^(\d{4}-\d{2}-\d{2})$/)?.[1]

  if (dateOnly) {
    return dateOnly
  }

  const offsetLocalDate = value.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s].*)[+-]\d{2}:?\d{2}$/)?.[1]

  if (offsetLocalDate) {
    return offsetLocalDate
  }

  const parsedDate = dayjs(value)

  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null
}

function dateIsBeyondIndefiniteDisplayHorizon(date: string, now: Dayjs = dayjs()): boolean {
  const parsedDate = dayjs(date, 'YYYY-MM-DD')

  if (!parsedDate.isValid()) {
    return false
  }

  return parsedDate.isAfter(
    dayjs(now).startOf('day').add(INDEFINITE_AVAILABILITY_CHANGE_DISPLAY_HORIZON_DAYS, 'day'),
    'day'
  )
}

function changeUsesFallbackDisplayIndefiniteEnd(
  change: AvailabilityChangeFields,
  now: Dayjs = dayjs()
): boolean {
  const endDate =
    getChangeDateInputValue(change?.end_date) ?? getChangeDateInputValue(change?.ends_at)

  if (!endDate || !dateIsBeyondIndefiniteDisplayHorizon(endDate, now)) {
    return false
  }

  const startDate =
    getChangeDateInputValue(change?.start_date) ??
    getChangeDateInputValue(change?.starts_at) ??
    getChangeDateInputValue(change?.date)

  if (!startDate) {
    return true
  }

  const parsedStartDate = dayjs(startDate, 'YYYY-MM-DD')
  const parsedEndDate = dayjs(endDate, 'YYYY-MM-DD')
  const displayHorizon = dayjs(now)
    .startOf('day')
    .add(INDEFINITE_AVAILABILITY_CHANGE_DISPLAY_HORIZON_DAYS, 'day')
  const rangeHorizon = parsedStartDate.add(
    INDEFINITE_AVAILABILITY_CHANGE_DISPLAY_HORIZON_DAYS,
    'day'
  )

  return (
    !parsedStartDate.isAfter(displayHorizon, 'day') || parsedEndDate.isAfter(rangeHorizon, 'day')
  )
}

export function changeHasStoredIndefiniteEnd(change: AvailabilityChangeFields): boolean {
  const endKind = change?.date_display?.end_kind

  if (endKind) {
    return endKind === AVAILABILITY_CHANGE_DATE_DISPLAY_END_KIND_STORED_INDEFINITE
  }

  if (change?.ends_indefinitely === true) {
    return true
  }

  const endDate = getChangeDateInputValue(change?.end_date)

  if (endDate) {
    return endDate === INDEFINITE_AVAILABILITY_CHANGE_END_DATE
  }

  const endsAtDate = getChangeDateInputValue(change?.ends_at)

  return endsAtDate === INDEFINITE_AVAILABILITY_CHANGE_END_DATE
}

export function changeEndsIndefinitely(
  change: AvailabilityChangeFields,
  now: Dayjs = dayjs()
): boolean {
  const usesIndefiniteEndLabel = change?.date_display?.uses_indefinite_end_label

  if (typeof usesIndefiniteEndLabel === 'boolean') {
    return usesIndefiniteEndLabel
  }

  const endKind = change?.date_display?.end_kind

  if (endKind) {
    return endKind !== AVAILABILITY_CHANGE_DATE_DISPLAY_END_KIND_FINITE
  }

  if (changeHasStoredIndefiniteEnd(change)) {
    return true
  }

  return changeUsesFallbackDisplayIndefiniteEnd(change, now)
}

function getChangeLocalDayValue(value: string | null | undefined): string | null {
  return getChangeDateInputValue(value)
}

function changeResolvesToSingleLocalDay(change: AvailabilityChangeFields): boolean {
  const isSingleDay = change?.date_display?.is_single_day

  if (typeof isSingleDay === 'boolean') {
    return isSingleDay
  }

  const startDate = getChangeLocalDayValue(getChangeStartDate(change))
  const endDate = getChangeLocalDayValue(getChangeEndDate(change))

  return Boolean(startDate && endDate && startDate === endDate)
}

export interface AvailabilityChangeDateRangeDisplayOptions {
  now?: Dayjs
  separator?: string
  formatDate?: (date: string | null | undefined) => string | null
  preserveFiniteSingleDayBeforeDisplayIndefinite?: boolean
}

export interface AvailabilityChangeDateRangeDisplay {
  label: string
  startLabel: string | null
  endLabel: string | null
  isSingleLocalDay: boolean
  isDisplayIndefinite: boolean
  hasStoredIndefiniteEnd: boolean
  usesIndefiniteEndLabel: boolean
}

export function getAvailabilityChangeDateRangeDisplay(
  change: AvailabilityChangeFields,
  {
    now = dayjs(),
    separator = ' to ',
    formatDate = formatAvailabilityDate,
    preserveFiniteSingleDayBeforeDisplayIndefinite = false,
  }: AvailabilityChangeDateRangeDisplayOptions = {}
): AvailabilityChangeDateRangeDisplay {
  const startLabel = formatDate(getChangeStartDate(change))
  const hasStoredIndefiniteEnd = changeHasStoredIndefiniteEnd(change)
  const isDisplayIndefinite = changeEndsIndefinitely(change, now)
  const isSingleLocalDay = changeResolvesToSingleLocalDay(change)
  const shouldUseSingleDayLabel =
    isSingleLocalDay &&
    (!isDisplayIndefinite ||
      (preserveFiniteSingleDayBeforeDisplayIndefinite && !hasStoredIndefiniteEnd))
  const endLabel =
    isDisplayIndefinite && !shouldUseSingleDayLabel
      ? INDEFINITE_AVAILABILITY_CHANGE_END_LABEL
      : formatDate(getChangeEndDate(change))

  if (!startLabel) {
    if (!endLabel) {
      return {
        label: 'Date not set',
        startLabel,
        endLabel,
        isSingleLocalDay,
        isDisplayIndefinite,
        hasStoredIndefiniteEnd,
        usesIndefiniteEndLabel: false,
      }
    }

    return {
      label: endLabel,
      startLabel,
      endLabel,
      isSingleLocalDay,
      isDisplayIndefinite,
      hasStoredIndefiniteEnd,
      usesIndefiniteEndLabel: endLabel === INDEFINITE_AVAILABILITY_CHANGE_END_LABEL,
    }
  }

  if (!endLabel || shouldUseSingleDayLabel || startLabel === endLabel) {
    return {
      label: startLabel,
      startLabel,
      endLabel,
      isSingleLocalDay,
      isDisplayIndefinite,
      hasStoredIndefiniteEnd,
      usesIndefiniteEndLabel: false,
    }
  }

  return {
    label: `${startLabel}${separator}${endLabel}`,
    startLabel,
    endLabel,
    isSingleLocalDay,
    isDisplayIndefinite,
    hasStoredIndefiniteEnd,
    usesIndefiniteEndLabel: endLabel === INDEFINITE_AVAILABILITY_CHANGE_END_LABEL,
  }
}

/** The saved change's id: calendar occurrences carry it as `change_id`. */
export function getChangeIdentifier(
  change: AvailabilityChange | AvailabilityCalendarOccurrence
): number
export function getChangeIdentifier(
  change: AvailabilityChange | AvailabilityCalendarOccurrence | null | undefined
): number | undefined
export function getChangeIdentifier(
  change: AvailabilityChangeFields | null | undefined
): number | string | undefined
export function getChangeIdentifier(
  change: AvailabilityChangeFields | null | undefined
): number | string | undefined {
  return change?.change_id ?? change?.id
}

export function changeHasTemporaryHours(
  change: AvailabilityChangeFields | null | undefined
): boolean {
  return Boolean(
    change?.temporary_hours?.pickup?.is_open || change?.temporary_hours?.delivery?.is_open
  )
}

export function getChangeDateRangeLabel(
  change: AvailabilityChangeFields,
  now: Dayjs = dayjs()
): string {
  return getAvailabilityChangeDateRangeDisplay(change, { now }).label
}

export function changeIsSingleDay(change: AvailabilityChangeFields): boolean {
  const startDate = dayjs(getChangeStartDate(change))
  const endDate = dayjs(getChangeEndDate(change))

  return startDate.isValid() && endDate.isValid() && startDate.isSame(endDate, 'day')
}

export function changeIsPastOneTime(
  change: AvailabilityChangeFields,
  now: Dayjs = dayjs()
): boolean {
  if (change?.repeats_annually) {
    return false
  }

  const endDate = dayjs(getChangeEndDate(change))

  return endDate.isValid() && endDate.isBefore(now, 'day')
}

function recurringDateForYear(templateDate: Dayjs, year: number): Dayjs {
  const month = templateDate.month()
  const daysInTargetMonth = templateDate.date(1).year(year).month(month).daysInMonth()
  const day = Math.min(templateDate.date(), daysInTargetMonth)

  return templateDate.date(1).year(year).month(month).date(day)
}

function getAvailabilityChangeResolvedOccurrenceRange(
  change: AvailabilityChangeFields
): OccurrenceRange | null {
  const templateStartsAt = dayjs(change?.starts_at)
  const templateEndsAt = dayjs(change?.ends_at)

  if (!templateStartsAt.isValid() || !templateEndsAt.isValid()) {
    return null
  }

  return { startsAt: templateStartsAt, endsAt: templateEndsAt }
}

function getAvailabilityChangeOccurrenceRangeForYear(
  change: AvailabilityChangeFields,
  year: number
): OccurrenceRange | null {
  const resolvedRange = getAvailabilityChangeResolvedOccurrenceRange(change)

  if (!resolvedRange) {
    return null
  }

  const { startsAt: templateStartsAt, endsAt: templateEndsAt } = resolvedRange

  if (!change?.repeats_annually) {
    return resolvedRange
  }

  const startsAt = recurringDateForYear(templateStartsAt, year)
  let endsAt = recurringDateForYear(templateEndsAt, year)

  if (!startsAt?.isValid() || !endsAt?.isValid()) {
    return null
  }

  if (endsAt.isBefore(startsAt)) {
    endsAt = endsAt.add(1, 'year')
  }

  return { startsAt, endsAt }
}

function parseLocalChangeDate(date: string, tz: string | undefined): Dayjs | null {
  const parsedDate = tz ? dayjs.tz(date, 'YYYY-MM-DD', tz) : dayjs(date, 'YYYY-MM-DD')

  return parsedDate.isValid() ? parsedDate : null
}

function getAvailabilityChangeLocalDateRangeForYear(
  change: AvailabilityChangeFields,
  year: number,
  tz: string | undefined
): OccurrenceRange | null {
  const startDateValue = getChangeDateInputValue(change?.start_date ?? change?.date)
  const endDateValue = getChangeDateInputValue(getChangeEndDate(change))

  if (!startDateValue || !endDateValue) {
    return getAvailabilityChangeOccurrenceRangeForYear(change, year)
  }

  const templateStartsAt = parseLocalChangeDate(startDateValue, tz)?.startOf('day')
  const templateEndsAt = parseLocalChangeDate(endDateValue, tz)?.endOf('day')

  if (!templateStartsAt?.isValid() || !templateEndsAt?.isValid()) {
    return getAvailabilityChangeOccurrenceRangeForYear(change, year)
  }

  if (!change?.repeats_annually) {
    return { startsAt: templateStartsAt, endsAt: templateEndsAt }
  }

  const startsAt = recurringDateForYear(templateStartsAt, year)
  let endsAt = recurringDateForYear(templateEndsAt, year)

  if (!startsAt?.isValid() || !endsAt?.isValid()) {
    return null
  }

  if (endsAt.isBefore(startsAt)) {
    endsAt = endsAt.add(1, 'year')
  }

  return { startsAt, endsAt }
}

function getAvailabilityChangeCandidateLocalDateRanges(
  change: AvailabilityChangeFields,
  now: Dayjs = dayjs(),
  tz?: string
): OccurrenceRange[] {
  if (change?.holiday_key) {
    const range = getAvailabilityChangeResolvedOccurrenceRange(change)

    return range ? [range] : []
  }

  if (!change?.repeats_annually) {
    const range = getAvailabilityChangeLocalDateRangeForYear(change, now.year(), tz)

    return range ? [range] : []
  }

  return [now.year() - 1, now.year(), now.year() + 1, now.year() + 2]
    .map((year) => getAvailabilityChangeLocalDateRangeForYear(change, year, tz))
    .filter((range): range is OccurrenceRange => range !== null)
}

function withAvailabilityChangeOccurrenceRange<TChange extends AvailabilityChangeFields>(
  change: TChange,
  range: OccurrenceRange
): TChange & { starts_at: string; ends_at: string } {
  return {
    ...change,
    starts_at: range.startsAt.toISOString(),
    ends_at: range.endsAt.toISOString(),
  }
}

function scopeAvailabilityOccurrenceToLocations(
  change: AvailabilityChangeFields,
  occurrence: AvailabilityChangeOccurrence | null,
  activeLocations: AvailabilityLocationRef[],
  timezone: string | undefined
): AvailabilityChangeOccurrence | null {
  if (!occurrence) {
    return null
  }

  if (!timezone || activeLocations.length === 0) {
    return occurrence
  }

  const matchingActiveLocations = getChangeMatchingActiveLocations(change, activeLocations)

  if (matchingActiveLocations.length === 0) {
    return null
  }

  const scopedLocations = matchingActiveLocations.filter(
    (location) => getLocationTimezone(location) === timezone
  )

  if (scopedLocations.length === 0) {
    return null
  }

  return {
    ...occurrence,
    all_locations: Boolean(
      occurrence.all_locations && scopedLocations.length >= matchingActiveLocations.length
    ),
    locations: scopedLocations,
    location_count: scopedLocations.length,
    total_active_locations:
      occurrence.total_active_locations ?? Math.max(matchingActiveLocations.length, 0),
  }
}

function mergeActiveAvailabilityOccurrences(
  change: AvailabilityChangeFields,
  occurrences: (AvailabilityChangeOccurrence | null)[],
  activeLocations: AvailabilityLocationRef[]
): AvailabilityChangeOccurrence | null {
  const sortedOccurrences = occurrences
    .filter((occurrence): occurrence is AvailabilityChangeOccurrence => occurrence !== null)
    .sort((left, right) => dayjs(left.ends_at).valueOf() - dayjs(right.ends_at).valueOf())
  const [firstOccurrence] = sortedOccurrences

  if (!firstOccurrence) {
    return null
  }

  const scopedLocations = [
    ...new Map(
      sortedOccurrences
        .flatMap((occurrence) => (Array.isArray(occurrence.locations) ? occurrence.locations : []))
        .map((location): [string, AvailabilityLocationRef] => [String(location.id), location])
    ).values(),
  ]

  if (scopedLocations.length === 0) {
    return firstOccurrence
  }

  const matchingActiveLocations = getChangeMatchingActiveLocations(change, activeLocations)

  return {
    ...firstOccurrence,
    all_locations: Boolean(
      change.all_locations && scopedLocations.length >= matchingActiveLocations.length
    ),
    locations: scopedLocations,
    location_count: scopedLocations.length,
    total_active_locations:
      firstOccurrence.total_active_locations ?? Math.max(matchingActiveLocations.length, 0),
  }
}

export function getAvailabilityChangeActiveOccurrence<TChange extends AvailabilityChangeFields>(
  change: TChange,
  now: Dayjs = dayjs(),
  tz?: string
): (TChange & { starts_at: string; ends_at: string }) | null {
  const range = getAvailabilityChangeCandidateLocalDateRanges(change, now, tz).find(
    ({ startsAt, endsAt }) => !startsAt.isAfter(now) && endsAt.isAfter(now)
  )

  return range ? withAvailabilityChangeOccurrenceRange(change, range) : null
}

export function getNextAvailabilityChangeOccurrence<TChange extends AvailabilityChangeFields>(
  change: TChange,
  now: Dayjs = dayjs(),
  tz?: string
): (TChange & { starts_at: string; ends_at: string }) | null {
  const range = getAvailabilityChangeCandidateLocalDateRanges(change, now, tz)
    .filter(({ startsAt }) => startsAt.isAfter(now))
    .sort((left, right) => left.startsAt.valueOf() - right.startsAt.valueOf())[0]

  return range ? withAvailabilityChangeOccurrenceRange(change, range) : null
}

export function getAvailabilityChangeActiveOccurrenceForLocations(
  change: AvailabilityChangeFields,
  now: Dayjs = dayjs(),
  activeLocations: AvailabilityLocationRef[] = []
): AvailabilityChangeOccurrence | null {
  const timezones = getChangeLocationTimezones(change, activeLocations)
  const candidateTimezones: (string | undefined)[] = timezones.length > 0 ? timezones : [undefined]
  const occurrences = candidateTimezones.map((timezone) =>
    scopeAvailabilityOccurrenceToLocations(
      change,
      getAvailabilityChangeActiveOccurrence(change, now, timezone),
      activeLocations,
      timezone
    )
  )

  return mergeActiveAvailabilityOccurrences(change, occurrences, activeLocations)
}

export function getNextAvailabilityChangeOccurrenceForLocations(
  change: AvailabilityChangeFields,
  now: Dayjs = dayjs(),
  activeLocations: AvailabilityLocationRef[] = []
): AvailabilityChangeOccurrence | null {
  const timezones = getChangeLocationTimezones(change, activeLocations)
  const candidateTimezones: (string | undefined)[] = timezones.length > 0 ? timezones : [undefined]

  return (
    candidateTimezones
      .map((timezone) =>
        scopeAvailabilityOccurrenceToLocations(
          change,
          getNextAvailabilityChangeOccurrence(change, now, timezone),
          activeLocations,
          timezone
        )
      )
      .filter((occurrence): occurrence is AvailabilityChangeOccurrence => occurrence !== null)
      .sort(
        (left, right) => dayjs(left.starts_at).valueOf() - dayjs(right.starts_at).valueOf()
      )[0] ?? null
  )
}

export function getAvailabilityChangeOccurrenceOnDate<TChange extends AvailabilityChangeFields>(
  change: TChange,
  dateYmd: string,
  tz?: string
): (TChange & { starts_at: string; ends_at: string }) | null {
  const dayStart = dayjs.tz(dateYmd, 'YYYY-MM-DD', tz).startOf('day')

  if (!dayStart.isValid()) {
    return null
  }

  const dayEnd = dayStart.endOf('day')
  const range = getAvailabilityChangeCandidateLocalDateRanges(change, dayStart, tz).find(
    ({ startsAt, endsAt }) => endsAt.isAfter(dayStart) && dayEnd.isAfter(startsAt)
  )

  return range ? withAvailabilityChangeOccurrenceRange(change, range) : null
}

export function getAvailabilityChangeOccurrencesInLocalDateRange<
  TChange extends AvailabilityChangeFields,
>(
  availabilityChanges: TChange[] | null = [],
  startDateYmd: string,
  endDateYmd: string | null,
  tz?: string
): (TChange & { starts_at: string; ends_at: string })[] {
  const rangeStart = dayjs.tz(startDateYmd, 'YYYY-MM-DD', tz).startOf('day')
  const rangeEnd = dayjs.tz(endDateYmd, 'YYYY-MM-DD', tz).endOf('day')

  if (!rangeStart.isValid() || !rangeEnd.isValid() || rangeStart.isAfter(rangeEnd)) {
    return []
  }

  return (availabilityChanges || [])
    .flatMap((change) => {
      if (change?.holiday_key) {
        const range = getAvailabilityChangeResolvedOccurrenceRange(change)

        return range ? [{ change, range }] : []
      }

      if (!change?.repeats_annually) {
        const range = getAvailabilityChangeLocalDateRangeForYear(change, rangeStart.year(), tz)

        return range ? [{ change, range }] : []
      }

      const occurrenceYears: number[] = []

      for (let year = rangeStart.year() - 1; year <= rangeEnd.year() + 1; year += 1) {
        occurrenceYears.push(year)
      }

      return occurrenceYears.flatMap((year) => {
        const range = getAvailabilityChangeLocalDateRangeForYear(change, year, tz)

        return range ? [{ change, range }] : []
      })
    })
    .filter(
      ({ range }) =>
        range.endsAt.isAfter(rangeStart) &&
        rangeEnd.isAfter(range.startsAt) &&
        range.startsAt.isValid() &&
        range.endsAt.isValid()
    )
    .map(({ change, range }) => withAvailabilityChangeOccurrenceRange(change, range))
    .sort((left, right) => dayjs(left.starts_at).valueOf() - dayjs(right.starts_at).valueOf())
}

/** `H:i` as `h:mm A`. */
export function formatTemporaryHour(time: string | null | undefined): string | null {
  if (!time) {
    return null
  }

  const parsedTime = dayjs(`2000-01-01 ${time}`)

  return parsedTime.isValid() ? parsedTime.format('h:mm A') : time
}

const temporaryHoursLanes: readonly (readonly [keyof AvailabilityTemporaryHours, string])[] = [
  ['pickup', 'Pickup'],
  ['delivery', 'Delivery'],
]

export function getTemporaryHoursSummary(change: AvailabilityChangeFields): string | null {
  const temporaryHours = change.temporary_hours

  if (!temporaryHours) {
    return null
  }

  const parts = temporaryHoursLanes
    .map(([key, label]) => {
      const hours = temporaryHours[key]

      if (!hours?.is_open) {
        return null
      }

      const open = formatTemporaryHour(hours.open)
      const close = formatTemporaryHour(hours.close)

      return open && close ? `${label}: ${open}-${close}` : label
    })
    .filter(isFilled)

  return parts.length > 0 ? parts.join(' · ') : null
}

/** A lane of the temporary hours form: the saved open lane, or a default lane to open or keep closed. */
export type TemporaryHoursFormLane =
  | AvailabilityTemporaryHoursWindow
  | AvailabilityTemporaryHoursWindowInput

export interface TemporaryHoursFormValue {
  pickup: TemporaryHoursFormLane
  delivery: TemporaryHoursFormLane
}

type OpenTemporaryHoursLane = Extract<AvailabilityTemporaryHoursWindowInput, { is_open: true }>

export function getDefaultTemporaryHours(): {
  pickup: OpenTemporaryHoursLane
  delivery: OpenTemporaryHoursLane
} {
  return {
    pickup: { is_open: true, open: '09:00', close: '17:00' },
    delivery: { is_open: true, open: '09:00', close: '17:00' },
  }
}

export function getTemporaryHoursFormValue(
  change: AvailabilityChangeFields | null | undefined
): TemporaryHoursFormValue {
  const temporaryHours: AvailabilityTemporaryHours = change?.temporary_hours ?? {}
  const defaults = getDefaultTemporaryHours()

  return {
    pickup: temporaryHours.pickup?.is_open
      ? temporaryHours.pickup
      : { ...defaults.pickup, is_open: false },
    delivery: temporaryHours.delivery?.is_open
      ? temporaryHours.delivery
      : { ...defaults.delivery, is_open: false },
  }
}

const availabilityEffectLabelOverrides: Record<string, string> = {
  'No pickup': 'Pickup pause',
  'No delivery': 'Delivery pause',
}

export function formatAvailabilityEffectLabel(label: string): string {
  return availabilityEffectLabelOverrides[label] ?? label
}

export function getAvailabilityChangeEffectLabels(change: AvailabilityChangeFields): string[] {
  if (Array.isArray(change.effect_labels) && change.effect_labels.length > 0) {
    return change.effect_labels.map(formatAvailabilityEffectLabel)
  }

  const labels: string[] = []

  if (change.store_closed) labels.push('Store closed')
  if (change.pickup_paused) labels.push('Pickup pause')
  if (change.delivery_paused) labels.push('Delivery pause')

  if (change.designers_choice_only) {
    labels.push("Designer's Choice only")
  }

  const temporaryHoursSummary = getTemporaryHoursSummary(change)
  if (temporaryHoursSummary) {
    labels.push(temporaryHoursSummary)
  }

  return labels.length > 0 ? labels : ['Availability change']
}

export function getAvailabilityChangeDisplayTitle(change: AvailabilityChangeFields): string {
  return getAvailabilityChangeEffectLabels(change).join(', ')
}

export function getChangeSearchText(change: AvailabilityChangeFields): string {
  return [
    getAvailabilityChangeDisplayTitle(change),
    getChangeLocationNames(change).join(' '),
    getChangeDateRangeLabel(change),
    change.store_closed ? 'closed store closed' : 'open',
    change.pickup_paused ? 'pickup pause no pickup pickup unavailable' : 'pickup available',
    change.delivery_paused
      ? 'delivery pause no delivery delivery unavailable'
      : 'delivery available',
    change.designers_choice_only ? "designers choice only designer's choice" : '',
    getTemporaryHoursSummary(change) ?? '',
    change.repeats_annually ? 'recurring yearly every year' : 'one time',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

/** The availability page's effect filter. */
export type AvailabilityChangeFilter =
  | 'all'
  | 'closed'
  | 'pickup_unavailable'
  | 'delivery_unavailable'
  | 'designers_choice_only'
  | 'temporary_hours'

export function changeMatchesAvailabilityFilter(
  change: AvailabilityChangeFields,
  filter: AvailabilityChangeFilter
): boolean {
  switch (filter) {
    case 'closed':
      return Boolean(change.store_closed)
    case 'pickup_unavailable':
      return Boolean(change.pickup_paused)
    case 'delivery_unavailable':
      return Boolean(change.delivery_paused)
    case 'designers_choice_only':
      return Boolean(change.designers_choice_only)
    case 'temporary_hours':
      return Boolean(getTemporaryHoursSummary(change))
    case 'all':
    default:
      return true
  }
}

/** `locationFilter` is `all` or an `AvailabilityLocationEntry` value. */
export function changeMatchesLocationFilter(
  change: AvailabilityChangeFields,
  locationFilter: string
): boolean {
  return (
    locationFilter === 'all' ||
    getChangeLocationEntries(change).some((location) => location.value === locationFilter)
  )
}

export function filterAvailabilityChanges<TChange extends AvailabilityChangeFields>(
  changes: TChange[],
  searchTerm: string,
  filter: AvailabilityChangeFilter,
  locationFilter: string
): TChange[] {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase()

  return changes.filter((change) => {
    if (!changeMatchesAvailabilityFilter(change, filter)) {
      return false
    }

    if (!changeMatchesLocationFilter(change, locationFilter)) {
      return false
    }

    return !normalizedSearchTerm || getChangeSearchText(change).includes(normalizedSearchTerm)
  })
}

export function getAvailabilityChangeCountText(count: number): string {
  return `${count} ${count === 1 ? 'change' : 'changes'}`
}

/** The latest `HH:mm` a temporary lane may open: an hour before it closes. */
export function getMaxTemporaryOpenTime(closeTime: string | null | undefined): string {
  if (!closeTime) return '23:59'

  const [hours, minutes] = closeTime.split(':')
  const closeHour = parseInt(hours ?? '', 10)
  const closeMinute = parseInt(minutes ?? '', 10)
  const openHour = Math.max(closeHour - 1, 0)
  const openMinute = closeHour - 1 < 0 ? 0 : closeMinute

  return `${openHour.toString().padStart(2, '0')}:${openMinute.toString().padStart(2, '0')}`
}

/** The earliest `HH:mm` a temporary lane may close: an hour after it opens. */
export function getMinTemporaryCloseTime(openTime: string | null | undefined): string {
  if (!openTime) return '00:00'

  const [hours, minutes] = openTime.split(':')
  const openHour = parseInt(hours ?? '', 10)
  const openMinute = parseInt(minutes ?? '', 10)
  const closeHour = Math.min(openHour + 1, 23)
  const closeMinute = openHour + 1 >= 24 ? 59 : openMinute

  return `${closeHour.toString().padStart(2, '0')}:${closeMinute.toString().padStart(2, '0')}`
}

export function getTemporaryHoursChangeLocationIds(
  change: AvailabilityChangeFields | null | undefined,
  activeLocationIds: string[]
): string[] {
  return getActiveChangeSelectedLocationIds(change, activeLocationIds)
}

/** The availability effects a change switches on. */
export interface AvailabilityEffectFlags {
  store_closed: boolean
  pickup_paused: boolean
  delivery_paused: boolean
  designers_choice_only: boolean
}

/**
 * The fields both editors' payloads set: a fixed-date change at the picked locations. The location
 * picker holds ids as strings, which `StoreAvailabilityChangeRequest`'s `integer` rule accepts.
 */
interface EditorAvailabilityChangePayload extends AvailabilityEffectFlags {
  organization_id: number
  location_ids: string[]
  schedule_type: 'fixed_date'
  repeats_annually: boolean
  holiday_key: null
  start_offset_days: null
  end_offset_days: null
}

/** The temporary hours form's values that its payload keeps. */
export interface TemporaryHoursPayloadValue extends AvailabilityEffectFlags {
  /** `YYYY-MM-DD` */
  start_date: string
  end_date: string
  temporary_hours: TemporaryHoursFormValue | null
  repeats_annually: boolean
}

export interface TemporaryHoursPayload extends EditorAvailabilityChangePayload {
  start_date: string
  end_date: string
  temporary_hours: TemporaryHoursFormValue | null
}

/** Editing keeps the saved change's effects; a new temporary hours change takes the form's. */
export function buildTemporaryHoursPayload({
  existingChange = null,
  organizationId,
  selectedLocationIds,
  value,
}: {
  existingChange?: Partial<AvailabilityEffectFlags> | null
  organizationId: number
  selectedLocationIds: string[]
  value: TemporaryHoursPayloadValue
}): TemporaryHoursPayload {
  const effects: Partial<AvailabilityEffectFlags> = existingChange ?? value

  return {
    ...value,
    organization_id: organizationId,
    location_ids: selectedLocationIds,
    schedule_type: 'fixed_date',
    store_closed: Boolean(effects.store_closed),
    pickup_paused: Boolean(effects.pickup_paused),
    delivery_paused: Boolean(effects.delivery_paused),
    designers_choice_only: Boolean(effects.designers_choice_only),
    repeats_annually: Boolean(value.repeats_annually),
    holiday_key: null,
    start_offset_days: null,
    end_offset_days: null,
  }
}

export function formatQuickCreateDateRange(
  selection: { startDate?: string | null; endDate?: string | null } | null | undefined
): string {
  const start = dayjs(selection?.startDate)
  const end = dayjs(selection?.endDate)

  if (!start.isValid() || !end.isValid()) {
    return 'Selected dates'
  }

  if (start.isSame(end, 'day')) {
    return start.format('ddd, MMM D, YYYY')
  }

  return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`
}

const quickCreateChooserWidth = 384
const quickCreateChooserEstimatedHeight = 300

/** Viewport pixels where the quick-create chooser opens, kept inside the viewport. */
export function getQuickCreatePopoverPosition(
  anchor: { left?: number; top?: number } | null | undefined
): { left: number; top: number; width: number } {
  const viewportPadding = 16
  const viewportWidth = window.innerWidth || 1024
  const viewportHeight = window.innerHeight || 768
  const maxLeft = Math.max(
    viewportPadding,
    viewportWidth - quickCreateChooserWidth - viewportPadding
  )
  const anchorLeft = anchor?.left ?? quickCreateChooserWidth / 2
  const unboundedLeft = anchorLeft
  const left = Math.min(Math.max(unboundedLeft, viewportPadding), maxLeft)
  const maxTop = Math.max(
    viewportPadding,
    viewportHeight - quickCreateChooserEstimatedHeight - viewportPadding
  )
  const top = Math.min(Math.max(anchor?.top ?? viewportPadding, viewportPadding), maxTop)

  return { left, top, width: quickCreateChooserWidth }
}

/** `getQuickCreateAvailabilityPayload()`: the caller adds the dates. */
export interface QuickCreateAvailabilityPayload extends EditorAvailabilityChangePayload {
  temporary_hours: null
}

export function getQuickCreateAvailabilityPayload({
  effects,
  organizationId,
  selectedLocationIds,
}: {
  effects: AvailabilityEffectFlags
  organizationId: number
  selectedLocationIds: string[]
}): QuickCreateAvailabilityPayload {
  return {
    organization_id: organizationId,
    location_ids: selectedLocationIds,
    schedule_type: 'fixed_date',
    store_closed: effects.store_closed,
    pickup_paused: effects.pickup_paused,
    delivery_paused: effects.delivery_paused,
    designers_choice_only: effects.designers_choice_only,
    temporary_hours: null,
    repeats_annually: false,
    holiday_key: null,
    start_offset_days: null,
    end_offset_days: null,
  }
}

export const quickCreateDefaultEffects: AvailabilityEffectFlags = {
  store_closed: false,
  pickup_paused: false,
  delivery_paused: false,
  designers_choice_only: false,
}

/** The effects the quick-create editor starts with: the edited change's, or preset ones. */
export function getQuickCreateEffects(
  change?: Partial<AvailabilityEffectFlags> | null
): AvailabilityEffectFlags {
  if (!change) {
    return quickCreateDefaultEffects
  }

  return {
    store_closed: Boolean(change.store_closed),
    pickup_paused: Boolean(change.pickup_paused),
    delivery_paused: Boolean(change.delivery_paused),
    designers_choice_only: Boolean(change.designers_choice_only),
  }
}

export const availabilityChangeDefaultSorting: SortingState = [{ id: 'start_date', desc: false }]

/** One-time changes that already ended sort after current and recurring ones. */
export function getAvailabilityChangeUpcomingSortGroup(change: AvailabilityChangeFields): 0 | 1 {
  const endDate = dayjs(getChangeEndDate(change))

  if (change?.repeats_annually || !endDate.isValid()) {
    return 0
  }

  return endDate.isBefore(dayjs(), 'day') ? 1 : 0
}

/** The start date, or a recurring change's next yearly start. */
export function getAvailabilityChangeSortDate(change: AvailabilityChangeFields): Dayjs {
  const startDate = dayjs(getChangeStartDate(change))

  if (!startDate.isValid()) {
    return startDate
  }

  if (!change?.repeats_annually) {
    return startDate
  }

  const today = dayjs()
  let nextOccurrence = startDate.year(today.year())

  if (nextOccurrence.isBefore(today, 'day')) {
    nextOccurrence = nextOccurrence.add(1, 'year')
  }

  return nextOccurrence
}

/** A table sorting function: upcoming changes soonest first, then ended ones most recent first. */
export function sortAvailabilityChangesByUpcomingStart(
  leftRow: { original: AvailabilityChangeFields },
  rightRow: { original: AvailabilityChangeFields }
): number {
  const leftChange = leftRow.original
  const rightChange = rightRow.original
  const leftGroup = getAvailabilityChangeUpcomingSortGroup(leftChange)
  const rightGroup = getAvailabilityChangeUpcomingSortGroup(rightChange)

  if (leftGroup !== rightGroup) {
    return leftGroup - rightGroup
  }

  const leftDate = getAvailabilityChangeSortDate(leftChange)
  const rightDate = getAvailabilityChangeSortDate(rightChange)

  if (!leftDate.isValid() && !rightDate.isValid()) {
    return 0
  }

  if (!leftDate.isValid()) {
    return 1
  }

  if (!rightDate.isValid()) {
    return -1
  }

  if (leftGroup === 1) {
    return rightDate.valueOf() - leftDate.valueOf()
  }

  return leftDate.valueOf() - rightDate.valueOf()
}
